import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { DataTable, DIALOG_TABLE_FRAME_CLASS } from '@/components/common/DataTable';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { FormDialogHeader, FormSection } from '@/components/common/FormDialog';
import {
  FIELD_LABEL_CLASS,
  FULL_ROW_FIELD_CLASS,
  LEADING_SYMBOL_CLASS,
} from '@/components/common/FormDialog/formDialog.constants';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import { getMonthToDateRange, getToday } from '@/utils/date';
import { filterRowsBySearch } from '@/utils/listSearch';
import { formatCount, formatNumber } from '@/utils/number';
import { candidatesQueryOptions, openCandidateBill } from '../api/publish.api';
import { usePublishBatch } from '../hooks/usePublishMutations';
import { BillButton } from './BillButton';
import type { Candidate } from '../types/publish.types';
import type { ColumnDef } from '@tanstack/react-table';

const money = (value: number): string => formatNumber(Math.round(value));

const readAmount = (raw: string | undefined): number | null => {
  if (raw === undefined || raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const isAmountInvalid = (raw: string | undefined): boolean =>
  raw !== undefined && raw.trim() !== '' && readAmount(raw) === null;

const DATE_FIELD_CLASS = cn(FIELD_CONTROL_CLASS, 'w-full hover:bg-card sm:h-10');

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PublishDialog({ open, onOpenChange }: PublishDialogProps) {
  const monthToDate = useMemo(() => getMonthToDateRange(), []);
  const [range, setRange] = useState(monthToDate);
  const today = useMemo(() => getToday(), []);

  const { data, isLoading } = useQuery(
    candidatesQueryOptions(open ? range.dateFrom : undefined, open ? range.dateTo : undefined),
  );

  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [comments, setComments] = useState('');
  const [overrides, setOverrides] = useState<{ dcr?: string; der?: string }>({});

  const [memberSearch, setMemberSearch] = useState('');

  const candidates = useMemo(
    () =>
      filterRowsBySearch(data, memberSearch, (candidate) => [
        candidate.fullName,
        candidate.hrEmpId,
        candidate.mobile,
        candidate.username,
        candidate.departmentName,
        candidate.divisionName,
        candidate.districtName,
        candidate.thanaName,
      ]),
    [data, memberSearch],
  );

  const totals = useMemo(() => {
    const chosen = (data ?? []).filter((c) => picked.has(c.userId));
    return {
      dcr: chosen.reduce((sum, c) => sum + c.dcrAmount, 0),
      der: chosen.reduce((sum, c) => sum + c.derAmount, 0),
      specimens: chosen.reduce((sum, c) => sum + c.specimenCount, 0),
      count: chosen.length,
    };
  }, [data, picked]);

  const publish = usePublishBatch(() => {
    setPicked(new Set());
    setComments('');
    setOverrides({});
    setRange(monthToDate);
    setMemberSearch('');
    onOpenChange(false);
  });

  function toggle(userId: number) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  const isEveryonePicked = picked.size > 0 && picked.size === candidates.length;
  const isSomePicked = picked.size > 0 && !isEveryonePicked;

  const columns: ColumnDef<Candidate>[] = [
    {
      id: 'pick',
      header: () => (
        <Checkbox
          checked={isEveryonePicked}
          indeterminate={isSomePicked}
          onCheckedChange={(on) =>
            setPicked(on === true ? new Set(candidates.map((c) => c.userId)) : new Set<number>())
          }
          aria-label="Pick everybody"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={picked.has(row.original.userId)}
          onCheckedChange={() => toggle(row.original.userId)}
          aria-label={`Pick ${row.original.fullName ?? 'member'}`}
        />
      ),
    },
    {
      id: 'member',
      header: 'ZM Name',
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.fullName ?? '—'}</span>
          <span className="text-xs text-muted-foreground">
            {[row.original.hrEmpId, row.original.designationName].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      id: 'mobile',
      header: 'Mobile',
      cell: ({ row }) => row.original.mobile ?? '—',
    },
    {
      id: 'department',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName ?? '—',
    },
    {
      id: 'division',
      header: 'Division',
      cell: ({ row }) => row.original.divisionName ?? '—',
    },
    {
      id: 'where',
      header: 'Where',
      cell: ({ row }) =>
        [row.original.thanaName, row.original.districtName].filter(Boolean).join(', ') || '—',
    },
    {
      id: 'dcr',
      header: 'DCR',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.dcrAmount)}</span>,
    },
    {
      id: 'der',
      header: 'DER',
      cell: ({ row }) => <span className="tabular-nums">{money(row.original.derAmount)}</span>,
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{money(row.original.totalAmount)}</span>
      ),
    },
    {
      id: 'specimens',
      header: 'SD Count',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.specimenCount)}</span>
      ),
    },
    {
      id: 'preview',
      header: 'Preview',
      cell: ({ row }) => (
        <BillButton
          label="Preview"
          memberName={row.original.fullName}
          onOpen={() => openCandidateBill(row.original.userId, range.dateFrom, range.dateTo)}
        />
      ),
    },
  ];

  const dcrOverride = readAmount(overrides.dcr);
  const derOverride = readAmount(overrides.der);
  const dcrAmount = dcrOverride ?? totals.dcr;
  const derAmount = derOverride ?? totals.der;
  const isDcrInvalid = isAmountInvalid(overrides.dcr);
  const isDerInvalid = isAmountInvalid(overrides.der);
  const isAmountEdited =
    (dcrOverride !== null && dcrOverride !== totals.dcr) ||
    (derOverride !== null && derOverride !== totals.der);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-5xl">
        <FormDialogHeader
          icon={Send}
          title="Publish"
          description="Approved, unpublished work in these dates. Zero-fare DCRs of the picked ZMs are added too."
        />

        <FormSection>
          <LabelledField label="Entry Date From" htmlFor="pub-entry-from">
            <DatePickerInput
              id="pub-entry-from"
              value={range.dateFrom}
              onChange={(dateFrom) => {
                setRange((prev) => ({ ...prev, dateFrom }));
                setPicked(new Set());
              }}
              maxDate={range.dateTo < today ? range.dateTo : today}
              className={DATE_FIELD_CLASS}
            />
          </LabelledField>
          <LabelledField label="Entry Date To" htmlFor="pub-entry-to">
            <DatePickerInput
              id="pub-entry-to"
              value={range.dateTo}
              onChange={(dateTo) => {
                setRange((prev) => ({ ...prev, dateTo }));
                setPicked(new Set());
              }}
              minDate={range.dateFrom}
              maxDate={today}
              className={DATE_FIELD_CLASS}
            />
          </LabelledField>
        </FormSection>

        <Input
          value={memberSearch}
          onChange={(event) => setMemberSearch(event.target.value)}
          placeholder="Find a ZM by name, ID or mobile"
          className={FIELD_CONTROL_CLASS}
          aria-label="Find a ZM"
        />

        <div className="min-w-0">
          <DataTable
            data={candidates}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Nobody has approved, unpublished work in this range"
            className={DIALOG_TABLE_FRAME_CLASS}
          />
        </div>

        <FormSection columnCount={4}>
          <ReadOnlyField label="ZMs" value={formatNumber(totals.count)} />
          <ReadOnlyField label="Specimens" value={formatNumber(totals.specimens)} />
          <LabelledField label="DCR Amount" htmlFor="pub-dcr-amount">
            <MoneyInput
              id="pub-dcr-amount"
              value={overrides.dcr ?? String(totals.dcr)}
              isInvalid={isDcrInvalid}
              onChange={(dcr) => setOverrides((prev) => ({ ...prev, dcr }))}
            />
          </LabelledField>
          <LabelledField label="DER Amount" htmlFor="pub-der-amount">
            <MoneyInput
              id="pub-der-amount"
              value={overrides.der ?? String(totals.der)}
              isInvalid={isDerInvalid}
              onChange={(der) => setOverrides((prev) => ({ ...prev, der }))}
            />
          </LabelledField>
          {isDcrInvalid || isDerInvalid ? (
            <p className={cn(FULL_ROW_FIELD_CLASS, 'text-sm text-destructive')}>
              An amount has to be a number, and cannot be negative.
            </p>
          ) : isAmountEdited ? (
            <p className={cn(FULL_ROW_FIELD_CLASS, 'text-sm text-muted-foreground')}>
              This differs from the {TAKA_SYMBOL} {formatNumber(totals.dcr + totals.der)} the picked
              ZMs add up to. The batch is recorded with what is typed here; the DCRs it sweeps do
              not change.
            </p>
          ) : null}

          <LabelledField label="Comment" htmlFor="pub-comment" isFullRow>
            <Input
              id="pub-comment"
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              placeholder="What this batch is, e.g. August-2026"
              className={FIELD_CONTROL_CLASS}
            />
          </LabelledField>
        </FormSection>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={picked.size === 0 || publish.isPending || isDcrInvalid || isDerInvalid}
            onClick={() => {
              publish.mutate({
                userIds: [...picked],
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                dcrAmount,
                derAmount,
                specimenCount: totals.specimens,
                ...(comments.trim() === '' ? {} : { comments: comments.trim() }),
              });
            }}
          >
            {publish.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Publish {picked.size === 0 ? '' : formatCount(picked.size, 'member')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface LabelledFieldProps {
  label: string;
  htmlFor: string;
  isFullRow?: boolean | undefined;
  children: ReactNode;
}

function LabelledField({ label, htmlFor, isFullRow = false, children }: LabelledFieldProps) {
  return (
    <div className={cn('grid gap-2', isFullRow && FULL_ROW_FIELD_CLASS)}>
      <label htmlFor={htmlFor} className={FIELD_LABEL_CLASS}>
        {label}
      </label>
      {children}
    </div>
  );
}

interface ReadOnlyFieldProps {
  label: string;
  value: string;
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  return (
    <div className="grid gap-2">
      <p className={FIELD_LABEL_CLASS}>{label}</p>
      <p className="flex h-10 items-center text-base font-medium tabular-nums">{value}</p>
    </div>
  );
}

interface MoneyInputProps {
  id: string;
  value: string;
  isInvalid?: boolean;
  onChange: (value: string) => void;
}

function MoneyInput({ id, value, isInvalid = false, onChange }: MoneyInputProps) {
  return (
    <div className="relative">
      <span className={LEADING_SYMBOL_CLASS} aria-hidden>
        {TAKA_SYMBOL}
      </span>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        aria-invalid={isInvalid}
        onChange={(event) => onChange(event.target.value)}
        className={cn(FIELD_CONTROL_CLASS, 'pl-8 tabular-nums')}
      />
    </div>
  );
}
